var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, DatePicker, Modal, Tooltip } from 'antd';
import classNames from 'classnames';
import FileSaver from "file-saver";
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime, Routes } from '@whatsper/texterchat-common';
import { API_PATH } from '../../../constants';
import CommonButton from '../../Chat/common/CommonButton';
import { useStore } from '../../../store/rootStore';
import { formatDaySecondsAsTime } from '../../../helpers/time';
import { getAxios } from '../../../backend';
import s from '../DashboardDrawer.module.scss';
const { FULL_REPORT_WINDOW_THRESHOLD_HEAVY_OP, FULL_REPORT_MAX_WINDOW_MS } = Routes.Stats.Chats;
;
const DownloadDataModal = ({ visible, onCancel, toggleDashboardDrawer }) => {
    const { i18n, t } = useTranslation();
    const { dashboard, settings } = useStore();
    const [loadingFullReport, setLoadingFullReport] = useState(false);
    const [loadingRawReport, setLoadingRawReport] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);
    const [forbiddenTimeError, setForbiddenTimeError] = useState(null);
    const isHeavyOperation = (afterTs, beforeTs) => {
        const windowMs = beforeTs - afterTs;
        return windowMs > FULL_REPORT_WINDOW_THRESHOLD_HEAVY_OP;
    };
    const checkForbiddenTime = () => {
        const schedule = settings.generalSettings.inbox.heavyOperationsForbiddenTime;
        if (!schedule)
            return null;
        return DateTime.Match.weekSchedule(schedule, new Date());
    };
    const formatTimeframe = (timeframe) => {
        return `${formatDaySecondsAsTime(timeframe.begin)} - ${formatDaySecondsAsTime(timeframe.end)}`;
    };
    const getWeekScheduleFormatted = () => {
        const schedule = settings.generalSettings.inbox.heavyOperationsForbiddenTime;
        if (!schedule)
            return {};
        const formatted = {};
        Object.entries(schedule).forEach(([day, timeframes]) => {
            const dayName = DateTime.WEEKDAY_NAMES_LIST[Number(day)];
            formatted[dayName] = timeframes
                .map(formatTimeframe)
                .join(', ');
        });
        return formatted;
    };
    const onChooseYesterday = () => {
        const yesterdayStart = moment().subtract(1, 'days').startOf('day').set({ hour: 9, minute: 0, second: 0 });
        const yesterdayEnd = moment().subtract(1, 'days').endOf('day').set({ hour: 23, minute: 59, second: 59 });
        setDateRange([yesterdayStart, yesterdayEnd]);
    };
    const onChooseLastWeek = () => {
        const end = moment().subtract(1, 'days').set({ hour: 23, minute: 59, second: 59 });
        const start = moment().subtract(7, 'days').set({ hour: 9, minute: 0, second: 0 });
        setDateRange([start, end]);
    };
    const onChooseLastMonth = () => {
        const end = moment().subtract(1, 'days').set({ hour: 23, minute: 59, second: 59 });
        const start = moment().subtract(30, 'days').set({ hour: 9, minute: 0, second: 0 });
        setDateRange([start, end]);
    };
    const validateAndProceed = (afterTimestamp, beforeTimestamp) => {
        setForbiddenTimeError(null);
        const windowMs = beforeTimestamp - afterTimestamp;
        if (windowMs > FULL_REPORT_MAX_WINDOW_MS) {
            setErrorMessage(t('requestedPeriodTooBig'));
            return false;
        }
        if (isHeavyOperation(afterTimestamp, beforeTimestamp)) {
            const forbiddenMatch = checkForbiddenTime();
            if (forbiddenMatch) {
                setForbiddenTimeError(forbiddenMatch);
                return false;
            }
        }
        return true;
    };
    const handleDownloadFullReportData = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!dateRange[0] || !dateRange[1])
            return;
        const afterTimestamp = dateRange[0].unix() * 1000;
        const beforeTimestamp = dateRange[1].unix() * 1000;
        if (!validateAndProceed(afterTimestamp, beforeTimestamp)) {
            return;
        }
        setLoadingFullReport(true);
        setDisabled(true);
        try {
            yield getDataAndGenerateCSVFile(true, afterTimestamp, beforeTimestamp, undefined, (_a = dashboard.settingsData) === null || _a === void 0 ? void 0 : _a.msgExtraActivityWindow);
            setLoadingFullReport(false);
            setDisabled(false);
            setErrorMessage(null);
        }
        catch (error) {
            console.error('Error downloading full report CSV file', error);
            const message = ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) || (error === null || error === void 0 ? void 0 : error.message) || t('unexpectedErrorTryAgain');
            setErrorMessage(message);
            setLoadingFullReport(false);
            setDisabled(false);
        }
    });
    const handleDownloadRawData = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!dateRange[0] || !dateRange[1])
            return;
        const afterTimestamp = dateRange[0].unix() * 1000;
        const beforeTimestamp = dateRange[1].unix() * 1000;
        if (!validateAndProceed(afterTimestamp, beforeTimestamp)) {
            return;
        }
        setLoadingRawReport(true);
        setDisabled(true);
        const inboxLanguage = ((_a = localStorage.getItem('currentLanguage')) === null || _a === void 0 ? void 0 : _a.toString()) || 'en';
        try {
            yield getDataAndGenerateCSVFile(false, afterTimestamp, beforeTimestamp, inboxLanguage, (_b = dashboard.settingsData) === null || _b === void 0 ? void 0 : _b.msgExtraActivityWindow);
            setErrorMessage(null);
            setDisabled(false);
            setLoadingRawReport(false);
        }
        catch (error) {
            console.error('Error downloading raw report CSV file', error);
            const message = ((_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message) || (error === null || error === void 0 ? void 0 : error.message) || t('unexpectedErrorTryAgain');
            setErrorMessage(message);
            setDisabled(false);
            setLoadingRawReport(false);
        }
    });
    const getDataAndGenerateCSVFile = (isFullReport, afterTimestamp, beforeTimestamp, language, msgExtraActivityWindow) => __awaiter(void 0, void 0, void 0, function* () {
        let csvData;
        const reportTypeForAPIPath = isFullReport ? 'full-report' : 'full-report-raw';
        const reportTypeForFileName = isFullReport ? 'Full' : 'Chats';
        const queryParamsCommon = {
            after: afterTimestamp,
            before: beforeTimestamp,
            msgExtraActivityWindow,
        };
        if (isFullReport) {
            const { data: response } = yield getAxios()
                .then((axios) => {
                return axios.get(`${API_PATH.STATS}/chats/status/${reportTypeForAPIPath}`, {
                    params: queryParamsCommon,
                });
            });
            csvData = generateFullReportCSV(response, afterTimestamp, beforeTimestamp);
        }
        else {
            const { data: response } = yield getAxios()
                .then((axios) => {
                return axios.get(`${API_PATH.STATS}/chats/status/${reportTypeForAPIPath}`, {
                    params: Object.assign(Object.assign({}, queryParamsCommon), { language })
                });
            });
            csvData = response.csv;
        }
        const BOM = '\uFEFF';
        const csvFile = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
        const fileName = generateFileName(afterTimestamp, beforeTimestamp, reportTypeForFileName);
        FileSaver.saveAs(csvFile, fileName);
    });
    const generateFullReportCSV = (data, afterTimestamp, beforeTimestamp) => {
        let csv = '';
        const TOTAL_DATA = data.totalChatsCounter;
        const BOT_DATA = data.chatsStartedFromBotCounter;
        const afterDate = new Date(afterTimestamp);
        afterDate.setHours(9, 0, 0, 0);
        const beforeDate = new Date(beforeTimestamp);
        beforeDate.setHours(23, 59, 59, 999);
        const afterDateStr = formatDateTime(afterDate);
        const beforeDateStr = formatDateTime(beforeDate);
        const dateRange = `${afterDateStr} - ${beforeDateStr}`;
        csv += `"${dateRange}"\n`;
        csv += `Category,Amount chats in Pending,Total Taken,Avg time to Resolve,Total times chats Resolved, Amount chats resolved, Amount bulk messsages delivered\n`;
        if (TOTAL_DATA.totalResolved !== 0) {
            csv += `Total numbers,${TOTAL_DATA.total},${TOTAL_DATA.totalTaken},${formatSeconds(TOTAL_DATA.avgTimeToResolve)},${TOTAL_DATA.totalResolved},${TOTAL_DATA.totalUniqueResolved},${TOTAL_DATA.amountBulksDelivered}\n`;
        }
        else {
            csv += `Total numbers,${TOTAL_DATA.total},${TOTAL_DATA.totalTaken},0,${TOTAL_DATA.totalResolved},0,${TOTAL_DATA.amountBulksDelivered}\n`;
        }
        if (BOT_DATA.resolved !== 0) {
            csv += `Chats started with bot start node,${BOT_DATA.count},${BOT_DATA.taken},${formatSeconds(BOT_DATA.avgTimeToResolve)},${BOT_DATA.resolved}\n`;
        }
        else {
            csv += `Chats started with bot start node,${BOT_DATA.count},${BOT_DATA.taken},0,${BOT_DATA.resolved}\n`;
        }
        csv += '\nBy Department';
        for (const [department, values] of Object.entries(data.totalCountsDepartments)) {
            csv += `\n${department},${values.count},${values.taken},,${values.resolved}`;
        }
        ;
        csv += '\n\nBy Agent';
        for (const [agent, values] of Object.entries(data.totalCountsAgents)) {
            if (values.resolved !== 0) {
                csv += `\n${agent},,${values.taken},${formatSeconds(values.avgTimeToResolve)},${values.resolved}`;
            }
            else {
                csv += `\n${agent},,${values.taken},0,${values.resolved}`;
            }
        }
        ;
        return csv;
    };
    const formatSeconds = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours === 0) {
            return `${minutes} m`;
        }
        else if (minutes === 0) {
            return `${hours} h`;
        }
        else {
            return `${hours} h ${minutes} m`;
        }
    };
    const formatDateTime = (date) => {
        const dateOptions = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        };
        const timeOptions = {
            hour: '2-digit',
            minute: '2-digit',
        };
        const formattedDate = date.toLocaleDateString(undefined, dateOptions);
        const formattedTime = date.toLocaleTimeString(undefined, timeOptions);
        return `${formattedDate} ${formattedTime}`;
    };
    const generateFileName = (afterTimestamp, beforeTimestamp, reportType) => {
        const afterDate = new Date(afterTimestamp);
        const beforeDate = new Date(beforeTimestamp);
        const afterFormattedDate = afterDate.toLocaleDateString('he', { day: '2-digit', month: '2-digit', year: 'numeric' }); // 22.07.2024
        const beforeFormattedDate = beforeDate.toLocaleDateString('he', { day: '2-digit', month: '2-digit', year: 'numeric' });
        let fileName = '';
        if (afterFormattedDate === beforeFormattedDate) {
            fileName = `${reportType} report ${afterFormattedDate}.csv`;
        }
        else {
            fileName = `${reportType} report ${afterFormattedDate}-${beforeFormattedDate}.csv`;
        }
        return fileName;
    };
    return (React.createElement(Modal, { open: visible, onCancel: onCancel, className: classNames(s['download-data-modal__container'], {
            [s['download-data-modal__container--rtl']]: i18n.dir() === 'rtl'
        }), width: 560, footer: null },
        React.createElement("div", { className: classNames(s['download-data-modal__header'], {
                [s['download-data-modal__header--rtl']]: i18n.dir() === 'rtl'
            }) },
            React.createElement("div", { className: s['download-data-modal__header--title'] },
                React.createElement(Trans, { i18nKey: "downloadDataToCSV" })),
            React.createElement("div", { className: classNames(s['download-data-modal__greenBorder'], {
                    [s['download-data-modal__greenBorder--rtl']]: i18n.dir() === 'rtl'
                }) })),
        React.createElement("div", { className: classNames(s['download-data-modal__body']) },
            React.createElement("div", { className: classNames(s['download-data-modal__pickDateWrapper']) },
                React.createElement("div", { className: classNames(s['download-data-modal__buttonsGroup'], {
                        [s['download-data-modal__buttonsGroup--rtl']]: i18n.dir() === 'rtl'
                    }) },
                    React.createElement(CommonButton, { action: onChooseYesterday, disabled: disabled },
                        React.createElement(Trans, { i18nKey: "yesterdayTime" })),
                    React.createElement(CommonButton, { action: onChooseLastWeek, disabled: disabled },
                        React.createElement(Trans, { i18nKey: "lastWeek" })),
                    React.createElement(CommonButton, { action: onChooseLastMonth, disabled: disabled },
                        React.createElement(Trans, { i18nKey: "lastMonth" }))),
                React.createElement("div", { className: classNames(s['download-data-modal_datePickersContainer'], {
                        [s['download-data-modal_datepickersContainer--rtl']]: i18n.dir() === 'rtl'
                    }) },
                    React.createElement(DatePicker, { format: "DD MMM YYYY", placeholder: t("chooseStartDate"), value: dateRange[0], onChange: (date) => {
                            const newDateRange = [...dateRange];
                            newDateRange[0] = date ? moment(date).set({ hour: 9, minute: 0, second: 0 }) : null;
                            setDateRange(newDateRange);
                        }, disabled: disabled }),
                    React.createElement(DatePicker, { format: "DD MMM YYYY", placeholder: t("chooseEndDate"), value: dateRange[1], disabledDate: (current) => {
                            if (dateRange[0]) {
                                const maxEndDate = dateRange[0].clone().add(FULL_REPORT_MAX_WINDOW_MS, 'milliseconds');
                                return current && (current < dateRange[0] || current > maxEndDate);
                            }
                            return true;
                        }, onChange: (date) => {
                            const newDateRange = [...dateRange];
                            newDateRange[1] = date ? moment(date).set({ hour: 23, minute: 59, second: 59 }) : null;
                            setDateRange(newDateRange);
                        }, disabled: disabled }))),
            React.createElement("div", { className: classNames(s['download-data-modal__downloadButtonsContainer']) },
                React.createElement(Button, { block: true, icon: React.createElement(DownloadOutlined, null), size: 'large', onClick: handleDownloadFullReportData, className: classNames(s['download-data-modal__downloadButton']), loading: loadingFullReport, disabled: disabled },
                    React.createElement(Trans, { i18nKey: "downloadData" })),
                React.createElement(Button, { block: true, icon: React.createElement(DownloadOutlined, null), size: 'large', onClick: handleDownloadRawData, type: 'dashed', className: classNames(s['download-data-modal__downloadButton']), loading: loadingRawReport, disabled: disabled },
                    React.createElement(Trans, { i18nKey: "downloadRawData" })),
                forbiddenTimeError && (React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "heavyOperationForbiddenNow" }), description: React.createElement("span", { className: s['download-data-modal__alertDescription'] },
                        React.createElement(Trans, { i18nKey: "heavyOperationForbiddenDescription", values: {
                                day: t(DateTime.WEEKDAY_NAMES_LIST[forbiddenTimeError.weekday]),
                                timeframe: formatTimeframe(forbiddenTimeError.period),
                            }, components: { b: React.createElement("strong", null) } }),
                        React.createElement(Tooltip, { overlayStyle: { maxWidth: 300 }, title: React.createElement("div", { className: s['download-data-modal__scheduleTooltip'] },
                                React.createElement("div", null,
                                    React.createElement(Trans, { i18nKey: "heavyOperationNotAllowedLabel" })),
                                Object.entries(getWeekScheduleFormatted()).map(([day, time]) => (React.createElement("div", { key: day },
                                    React.createElement(Trans, { i18nKey: day }),
                                    ": ",
                                    time)))) },
                            React.createElement(Button, { className: s['download-data-modal__scheduleButton'], size: "small", type: "primary" },
                                React.createElement(Trans, { i18nKey: "weekSchedule" })))), className: classNames(s['download-data-modal__alert'], {
                        [s['download-data-modal__alert--rtl']]: i18n.dir() === 'rtl'
                    }), type: "error", icon: React.createElement(WarningOutlined, null), showIcon: true })),
                errorMessage && !forbiddenTimeError ? (React.createElement(Alert, { message: errorMessage, type: "error", showIcon: true, className: classNames(s['download-data-modal__alert'], {
                        [s['download-data-modal__alert--rtl']]: i18n.dir() === 'rtl'
                    }) })) : null))));
};
export default observer(DownloadDataModal);
